import * as React from "react";
import {
    required,
    useShowController,
    List, 
    Datagrid, 
    EditButton,
    Show,
    Edit,
    Create,
    Filter,
    TextField, 
    SelectInput,
    NumberField,
    ImageField,
    FormDataConsumer,
    SimpleShowLayout,
    SimpleForm,
    TextInput,
    NumberInput,
    RichTextField,
    BulkDeleteButton,
    ReferenceField,
    ReferenceInput,
    AutocompleteInput,
    FunctionField,
    DateField,
    BooleanField,
    ChipField,
    BooleanInput,
    DateTimeInput,
    useUnique,
} from 'react-admin';
import { Grid } from '@mui/material';
import Breadcrumb from '../components/breadcrumb';
import PostShowActions from "../components/postShowActions";

const BulkActionButtons = props => (
    <BulkDeleteButton {...props} undoable={false} />
)

export const CouponList = props => (
    <Grid container spacing={2}>
        <Breadcrumb title={ 'Coupons' } />
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <List filters={<CouponFilter />} title="Coupons">
                    <Datagrid rowClick="show" bulkActionButtons={<BulkActionButtons />}>
                        <TextField source="name" />
                        <TextField source="code" />
                        <ReferenceField label="User" source="user._id" reference="users" link="show" >
                            <TextField source="fullname" />
                            <br />
                            <TextField source="email" />
                        </ReferenceField>
                        <ReferenceField label="Season" source="ticket_type._id" reference="ticket-types" link={ false } >
                            <TextField source="name" />
                        </ReferenceField>
                        <TextField source="priority" />
                        <BooleanField source="is_used" />
                        <TextField source="description" />
                        <ReferenceField label="Status" source="status" reference="status" link={ false } >
                            <FunctionField label="Status" render={
                                record => record.id === 'Active' ? 
                                    (<ChipField source="title" color="primary" />) : (<ChipField source="title" color="default" />) 
                            } />
                        </ReferenceField>
                        <DateField label="Expired At" source="expired_at" showTime={true} emptyText="N/A" />
                        <DateField label="Created At" source="createdAt" showTime={true} emptyText="N/A" />
                        <EditButton />
                    </Datagrid>
                </List>
            </Grid>
        </Grid>
    </Grid>
);

export const CouponShow = (props) => {
    const {
        record
    } = useShowController(props);

    return (
        <Show {...props} actions={<PostShowActions resource="coupons" isEdit={true} />}>
            <Grid container paddingBottom={2} spacing={2}>
                <Grid item xs={12}>
                    <SimpleShowLayout>
                        <TextField source="name" />
                        <TextField source="code" />
                        <ReferenceField label="User" source="user._id" reference="users" link="show" >
                            <TextField source="fullname" />
                            <br />
                            <TextField source="email" />
                        </ReferenceField>
                        <ReferenceField label="Season" source="ticket_type._id" reference="ticket-types" link={ false } >
                            <TextField source="name" />
                        </ReferenceField>
                        <TextField source="priority" />
                        <BooleanField source="is_used" />
                        <TextField source="description" />
                        <ReferenceField label="Status" source="status" reference="status" link={ false } >
                            <FunctionField label="Status" render={
                                record => record.id === 'Active' ? 
                                    (<ChipField source="title" color="primary" />) : (<ChipField source="title" color="default" />) 
                            } />
                        </ReferenceField>
                        <DateField label="Expired At" source="expired_at" showTime={true} emptyText="N/A" />
                        <DateField label="Created At" source="createdAt" showTime={true} emptyText="N/A" />
                    </SimpleShowLayout>
                </Grid>
            </Grid>
        </Show>
    );
}

export const CouponEdit = props => {
    return (
        <Edit {...props} actions={<PostShowActions resource="coupons" />}>
            <CreateEditForm {...props} />
        </Edit>
    );
}

export const CouponCreate = props => {
    return (
        <Create {...props} actions={<PostShowActions resource="coupons" />}>
            <CreateEditForm {...props} />
        </Create>
    );
}

const CreateEditForm = props => {
    const unique = useUnique();

    return (
        <SimpleForm {...props}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextInput source="name" validate={required()} fullWidth />
                </Grid>
                <Grid item xs={12}>
                    <TextInput source="code" validate={[unique(), required()]} fullWidth />
                </Grid>
                <Grid item xs={12}>
                    <ReferenceInput source="user" reference="users" perPage={9999} fullWidth>
                        <AutocompleteInput validate={[required()]} format={ formatReferenceDisplay } filterToQuery={filterToQuery} optionText={choice => `${choice.fullname} (${choice.email})`} optionValue="id" fullWidth />
                    </ReferenceInput>
                </Grid>
                <Grid item xs={12}>
                    <ReferenceInput source="ticket_type" sort={{field: 'active', order: 'DESC'}} reference="ticket-types">
                        <SelectInput validate={[required()]} format={ formatReferenceDisplay } label="Season" optionText="name" optionValue="id" fullWidth />
                    </ReferenceInput>
                </Grid>
                <Grid item xs={12}>
                    <TextInput multiline={true} source="description" defaultValue="" fullWidth />
                </Grid>
                <Grid item xs={12}>
                    <DateTimeInput source="expired_at" fullWidth />
                </Grid>
                <Grid item xs={12}>
                    <BooleanInput source="is_used" validate={required()} defaultValue={false} fullWidth />
                </Grid>
                <Grid item xs={12}>
                    <NumberInput step={1} source="priority" validate={required()} defaultValue={0} fullWidth />
                </Grid>
                <Grid item xs={12}>
                    <ReferenceInput source="status" reference="status">
                        <AutocompleteInput validate={[required()]} filterToQuery={filterToQuery} optionText="title" optionValue="id" fullWidth />
                    </ReferenceInput>
                </Grid>
            </Grid>
        </SimpleForm>
    );
}

export const CouponFilter= props => (
    <Filter { ...props }>
        <TextInput label="Search" source="keyword" alwaysOn />
        <ReferenceInput source="user" reference="users" alwaysOn>
            <AutocompleteInput filterToQuery={filterToQuery} optionText={choice => `${choice.fullname} (${choice.email})`} optionValue="id" fullWidth />
        </ReferenceInput>
    </Filter >
);

const formatReferenceDisplay = function (value) {
    return value && value._id ? value._id : value;
};

const filterToQuery = searchText => ({ keyword: searchText });